// Shopify renders the storefront; these are the only values our page needs.
export const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN || '';
export const SHOPIFY_STOREFRONT_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN || '';

export const SHOPIFY_API_VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || '2026-07';

export const isShopConfigured = () => Boolean(SHOPIFY_DOMAIN && SHOPIFY_STOREFRONT_TOKEN);

// Shopify wants a single E.164 number; returns null when we cannot build a valid one.
export const toE164 = (countryCode, phoneNumber) => {
  const digits = `${countryCode || ''}${phoneNumber || ''}`.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15 ? `+${digits}` : null;
};

// Patients have one "name" field; Shopify's address wants first and last.
export const splitName = (fullName) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

/**
 * Everything Shopify can accept up-front about the signed-in patient, so the
 * checkout is prefilled instead of asking for details they already gave us.
 *
 * The street address is deliberately left out. A patient profile holds one
 * free-text address and no city, state or pincode, so sending it filled the
 * street line and left the rest blank - which also stops Shopify's own address
 * search from filling all four fields in one click. An empty address field is
 * the better starting point.
 */
export const buildBuyerIdentity = (patient) => {
  if (!patient) return null;
  const identity = {};
  if (patient.email) identity.email = String(patient.email).trim().toLowerCase();
  const phone = toE164(patient.countryCode, patient.phoneNumber);
  if (phone) identity.phone = phone;

  const { firstName, lastName } = splitName(patient.name);
  const deliveryAddress = {};
  if (firstName) deliveryAddress.firstName = firstName;
  if (lastName) deliveryAddress.lastName = lastName;
  if (phone) deliveryAddress.phone = phone;
  if (Object.keys(deliveryAddress).length) {
    deliveryAddress.country = 'India';
    identity.deliveryAddressPreferences = [{ deliveryAddress }];
  }
  return Object.keys(identity).length ? identity : null;
};

export const formatMoney = (amount, currency) => {
  const value = Number(amount) || 0;
  const code = currency || 'INR';
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 2 }).format(value);
  } catch (error) {
    return `${code} ${value.toFixed(2)}`;
  }
};

export const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Shopify's order-level return states, worded the way a patient reads them.
const RETURN_LABELS = {
  return_requested: 'Return requested',
  in_progress: 'Return in progress',
  inspection_complete: 'Return inspected',
  returned: 'Returned',
  return_failed: 'Return failed',
};

// The reasons Shopify accepts on a return request, in the patient's words.
export const RETURN_REASON_OPTIONS = [
  { value: 'DEFECTIVE', label: 'Arrived damaged or faulty' },
  { value: 'WRONG_ITEM', label: 'Wrong item was sent' },
  { value: 'NOT_AS_DESCRIBED', label: 'Not as described' },
  { value: 'SIZE_TOO_SMALL', label: 'Size too small' },
  { value: 'SIZE_TOO_LARGE', label: 'Size too large' },
  { value: 'UNWANTED', label: 'No longer needed' },
  { value: 'OTHER', label: 'Another reason' },
];

export const returnLabel = (status) =>
  RETURN_LABELS[String(status || '').toLowerCase()] || statusLabel(status);

// Shopify statuses arrive as "partially_refunded", "unfulfilled" and so on.
export const statusLabel = (status) =>
  String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()) || '-';
